import { useState } from 'react';
import {
  Clock,
  KeyRound,
  Mail,
  MoreHorizontal,
  Search,
  Shield,
  ShieldOff,
  Trash2,
  UserCog,
  UserPlus,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useDebouncedValue } from '@/hooks/use-debounced-value';
import { extractErrorMessage } from '@/lib/api-client';
import { formatRelative, initialsFromName } from '@/lib/format';
import { useAuthStore } from '@/features/auth/store';
import {
  useAdminResetPassword,
  useCancelInvitation,
  useDeleteUser,
  useInvitationsList,
  useUpdateUser,
  useUsersList,
  type UserItem,
} from './api';
import { InviteDialog } from './invite-dialog';

const ROLE_LABEL: Record<string, { label: string; variant: 'info' | 'secondary' | 'warning' }> = {
  admin: { label: 'Administrador', variant: 'info' },
  manager: { label: 'Gestor', variant: 'secondary' },
  driver: { label: 'Motorista', variant: 'warning' },
  workshop: { label: 'Oficina', variant: 'secondary' },
};

export function UsersPage() {
  const currentUserId = useAuthStore((s) => s.user?.id);
  const [search, setSearch] = useState('');
  const [inviteOpen, setInviteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<UserItem | null>(null);
  const [resetTarget, setResetTarget] = useState<UserItem | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const debouncedSearch = useDebouncedValue(search, 300);
  const usersQuery = useUsersList({ page: 1, limit: 50, q: debouncedSearch || undefined });
  const invitationsQuery = useInvitationsList();
  const updateUser = useUpdateUser();
  const deleteUser = useDeleteUser();
  const resetPassword = useAdminResetPassword();
  const cancelInvitation = useCancelInvitation();

  const users = usersQuery.data?.data ?? [];
  const invitations = invitationsQuery.data?.data ?? [];
  const pendingInvites = invitations.filter((i) => i.status === 'pending');

  const toggleActive = async (user: UserItem) => {
    try {
      await updateUser.mutateAsync({ id: user.id, active: !user.active });
      toast.success(user.active ? `${user.name} desativado` : `${user.name} reativado`);
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteUser.mutateAsync(deleteTarget.id);
      toast.success(`${deleteTarget.name} removido`);
      setDeleteTarget(null);
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  return (
    <div className="page">
      <header className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div className="space-y-1">
          <div className="inline-flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
            <UserCog className="size-3.5" />
            Administração
          </div>
          <h1 className="text-2xl font-semibold tracking-tight md:text-3xl">
            Usuários e convites
          </h1>
          <p className="text-sm text-muted-foreground">
            Gerencie quem tem acesso ao painel da sua empresa.
          </p>
        </div>
        <Button onClick={() => setInviteOpen(true)} size="lg">
          <UserPlus className="size-4" /> Convidar
        </Button>
      </header>

      <Tabs defaultValue="users">
        <TabsList>
          <TabsTrigger value="users">
            <UserCog className="size-4" /> Usuários ({users.length})
          </TabsTrigger>
          <TabsTrigger value="invites">
            <Mail className="size-4" /> Convites pendentes ({pendingInvites.length})
          </TabsTrigger>
        </TabsList>

        {/* Tab: Usuários */}
        <TabsContent value="users">
          <Card>
            <CardContent className="p-4">
              <div className="relative mb-4">
                <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Buscar por nome ou email…"
                  className="pl-9"
                />
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border bg-muted/40 text-left text-xs uppercase tracking-wider text-muted-foreground">
                      <th className="px-4 py-3 font-medium">Usuário</th>
                      <th className="px-4 py-3 font-medium">Papel</th>
                      <th className="px-4 py-3 font-medium">Status</th>
                      <th className="px-4 py-3 font-medium">Último acesso</th>
                      <th className="w-10 px-4 py-3" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {usersQuery.isLoading &&
                      Array.from({ length: 3 }).map((_, i) => (
                        <tr key={i}>
                          {Array.from({ length: 5 }).map((_, j) => (
                            <td key={j} className="px-4 py-3">
                              <Skeleton className="h-4 w-full" />
                            </td>
                          ))}
                        </tr>
                      ))}

                    {users.map((u) => {
                      const role = ROLE_LABEL[u.role];
                      const isSelf = u.id === currentUserId;
                      return (
                        <tr key={u.id} className="transition-colors hover:bg-muted/30">
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              <Avatar className="size-9">
                                <AvatarFallback>{initialsFromName(u.name)}</AvatarFallback>
                              </Avatar>
                              <div className="min-w-0">
                                <p className="truncate text-sm font-medium">
                                  {u.name} {isSelf && <span className="text-xs text-muted-foreground">(você)</span>}
                                </p>
                                <p className="truncate text-xs text-muted-foreground">{u.email}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <Badge variant={role?.variant ?? 'outline'}>{role?.label ?? u.role}</Badge>
                          </td>
                          <td className="px-4 py-3">
                            {u.active ? (
                              <Badge variant="success">Ativo</Badge>
                            ) : (
                              <Badge variant="outline">Inativo</Badge>
                            )}
                          </td>
                          <td className="px-4 py-3 text-muted-foreground text-xs">
                            {u.lastLoginAt ? formatRelative(u.lastLoginAt) : 'Nunca'}
                          </td>
                          <td className="px-4 py-3 text-right">
                            {!isSelf && (
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="icon" className="size-8">
                                    <MoreHorizontal className="size-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem onClick={() => toggleActive(u)}>
                                    {u.active ? (
                                      <><ShieldOff className="size-4" /> Desativar</>
                                    ) : (
                                      <><Shield className="size-4" /> Reativar</>
                                    )}
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onClick={() => {
                                      setNewPassword('');
                                      setConfirmPassword('');
                                      setResetTarget(u);
                                    }}
                                  >
                                    <KeyRound className="size-4" /> Redefinir senha
                                  </DropdownMenuItem>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem
                                    onClick={() => setDeleteTarget(u)}
                                    className="text-destructive"
                                  >
                                    <Trash2 className="size-4" /> Remover
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Convites */}
        <TabsContent value="invites">
          {pendingInvites.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center gap-3 p-10 text-center">
                <Mail className="size-8 text-muted-foreground" />
                <p className="text-sm font-medium">Nenhum convite pendente</p>
                <p className="text-xs text-muted-foreground">
                  Convide novos usuários clicando em "Convidar".
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {pendingInvites.map((inv) => (
                <Card key={inv.id}>
                  <CardContent className="flex items-center justify-between gap-4 p-4">
                    <div className="flex items-center gap-3">
                      <div className="flex size-10 items-center justify-center rounded-full bg-primary/10 text-primary">
                        <Mail className="size-5" />
                      </div>
                      <div>
                        <p className="text-sm font-medium">{inv.email}</p>
                        <p className="text-xs text-muted-foreground">
                          <Badge variant="secondary" className="mr-1">{ROLE_LABEL[inv.role]?.label ?? inv.role}</Badge>
                          <Clock className="inline size-3" /> Expira {formatRelative(inv.expiresAt)}
                        </p>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="size-8 text-destructive"
                      onClick={async () => {
                        try {
                          await cancelInvitation.mutateAsync(inv.id);
                          toast.success('Convite cancelado');
                        } catch (err) {
                          toast.error(extractErrorMessage(err));
                        }
                      }}
                    >
                      <X className="size-4" />
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <InviteDialog open={inviteOpen} onOpenChange={setInviteOpen} />

      <Dialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Remover usuário</DialogTitle>
            <DialogDescription>
              Tem certeza que deseja remover <strong>{deleteTarget?.name}</strong>? O acesso será revogado imediatamente.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancelar</Button>
            <Button variant="destructive" onClick={confirmDelete} loading={deleteUser.isPending}>Remover</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!resetTarget} onOpenChange={(open) => !open && setResetTarget(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Redefinir senha</DialogTitle>
            <DialogDescription>
              Defina uma nova senha para <strong>{resetTarget?.name}</strong>.
              Todas as sessões ativas serão encerradas e um e-mail será enviado.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="new-pw">Nova senha *</Label>
              <Input
                id="new-pw"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="mínimo 10 caracteres"
                autoComplete="new-password"
              />
              {newPassword.length > 0 && newPassword.length < 10 && (
                <p className="text-xs text-destructive">Mínimo de 10 caracteres.</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="confirm-pw">Confirmar senha *</Label>
              <Input
                id="confirm-pw"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                autoComplete="new-password"
              />
              {confirmPassword.length > 0 && newPassword !== confirmPassword && (
                <p className="text-xs text-destructive">As senhas não coincidem.</p>
              )}
            </div>
            <div className="rounded-md border border-warning/40 bg-warning/5 p-2 text-xs text-warning-foreground">
              ⚠️ Anote ou compartilhe a senha com o usuário por um canal seguro. Ele será
              avisado por e-mail mas a senha em si não vai junto.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetTarget(null)}>Cancelar</Button>
            <Button
              onClick={async () => {
                if (!resetTarget) return;
                try {
                  await resetPassword.mutateAsync({
                    id: resetTarget.id,
                    newPassword,
                  });
                  toast.success(`Senha de ${resetTarget.name} redefinida`);
                  setResetTarget(null);
                  setNewPassword('');
                  setConfirmPassword('');
                } catch (err) {
                  toast.error(extractErrorMessage(err));
                }
              }}
              loading={resetPassword.isPending}
              disabled={
                newPassword.length < 10 ||
                newPassword !== confirmPassword ||
                resetPassword.isPending
              }
            >
              Redefinir senha
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
